import React, { useEffect, useRef, useCallback } from 'react';
import cytoscape, {
  type Core,
  type NodeSingular,
  type EdgeSingular,
  type ElementDefinition,
} from 'cytoscape';
import fcose from 'cytoscape-fcose';
import dagre from 'cytoscape-dagre';
import cola from 'cytoscape-cola';
import { useUIStore } from '@/stores/uiStore';
import { useUpdateNodePosition } from '@/hooks/useGraph';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import type { GraphData, GraphNode } from '@/types';

// Register Cytoscape layouts once
cytoscape.use(fcose as Parameters<typeof cytoscape.use>[0]);
cytoscape.use(dagre as Parameters<typeof cytoscape.use>[0]);
cytoscape.use(cola as Parameters<typeof cytoscape.use>[0]);

// ─── Type colours ─────────────────────────────────────────────────────────────

const NODE_COLORS: Record<string, string> = {
  skill:        '#3b82f6',
  knowledge:    '#8b5cf6',
  experience:   '#22c55e',
  tool:         '#f97316',
  relationship: '#ec4899',
  credential:   '#eab308',
  domain:       '#14b8a6',
};

const STATUS_BORDERS: Record<string, string> = {
  active:     '#22c55e',
  growing:    '#3b82f6',
  dormant:    '#f59e0b',
  deprecated: '#ef4444',
};

// ─── Build Cytoscape element definitions ──────────────────────────────────────

function buildElements(graphData: GraphData): ElementDefinition[] {
  const nodeEls: ElementDefinition[] = graphData.nodes.map((node: GraphNode) => ({
    group: 'nodes',
    data: {
      id: node.id,
      label: node.data.label,
      type: node.data.type,
      status: node.data.status,
      strength: node.data.strength,
      capabilityId: node.data.capabilityId,
    },
    position: node.position.x !== 0 || node.position.y !== 0 ? node.position : undefined,
  }));

  const edgeEls: ElementDefinition[] = graphData.edges.map((edge) => ({
    group: 'edges',
    data: {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.data.label ?? '',
      type: edge.data.type,
      weight: edge.data.weight,
    },
  }));

  return [...nodeEls, ...edgeEls];
}

// ─── Cytoscape stylesheet ─────────────────────────────────────────────────────

const buildStylesheet = () => [
  {
    selector: 'node',
    style: {
      'background-color': (ele: NodeSingular) =>
        NODE_COLORS[(ele.data('type') as string)] ?? '#64748b',
      'border-color': (ele: NodeSingular) =>
        STATUS_BORDERS[(ele.data('status') as string)] ?? '#94a3b8',
      'border-width': 2.5,
      'width': (ele: NodeSingular) => {
        const strength = (ele.data('strength') as number) || 50;
        return Math.max(32, Math.min(72, 24 + strength * 0.48));
      },
      'height': (ele: NodeSingular) => {
        const strength = (ele.data('strength') as number) || 50;
        return Math.max(32, Math.min(72, 24 + strength * 0.48));
      },
      'label': 'data(label)',
      'font-size': 10,
      'font-family': 'Inter, system-ui, sans-serif',
      'font-weight': 600,
      'text-valign': 'bottom',
      'text-halign': 'center',
      'text-margin-y': 4,
      'color': '#475569',
      'text-max-width': '80px',
      'text-wrap': 'ellipsis',
      'overlay-padding': 4,
      'transition-property': 'background-color, border-color, width, height',
      'transition-duration': 200,
    } as cytoscape.StylesheetStyle['style'],
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': 4,
      'border-color': '#1B3A6B',
      'box-shadow': '0 0 20px rgba(27,58,107,0.4)',
    } as cytoscape.StylesheetStyle['style'],
  },
  {
    selector: 'node:hover',
    style: {
      'border-width': 3.5,
      'cursor': 'pointer',
    } as cytoscape.StylesheetStyle['style'],
  },
  {
    selector: 'edge',
    style: {
      'width': (ele: EdgeSingular) => {
        const w = (ele.data('weight') as number) || 0.5;
        return 1 + w * 3;
      },
      'line-color': '#94a3b8',
      'target-arrow-color': '#94a3b8',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'label': 'data(label)',
      'font-size': 9,
      'font-family': 'Inter, system-ui, sans-serif',
      'color': '#94a3b8',
      'text-rotation': 'autorotate',
      'text-margin-y': -8,
      'opacity': 0.7,
    } as cytoscape.StylesheetStyle['style'],
  },
  {
    selector: 'edge:selected',
    style: {
      'line-color': '#E87722',
      'target-arrow-color': '#E87722',
      'opacity': 1,
    } as cytoscape.StylesheetStyle['style'],
  },
  {
    selector: '.highlighted',
    style: {
      'opacity': 1,
      'line-color': '#1B3A6B',
      'target-arrow-color': '#1B3A6B',
    } as cytoscape.StylesheetStyle['style'],
  },
  {
    selector: '.faded',
    style: {
      'opacity': 0.2,
    } as cytoscape.StylesheetStyle['style'],
  },
];

// ─── GraphView ────────────────────────────────────────────────────────────────

interface GraphViewProps {
  graphData: GraphData;
  onNodeClick?: (nodeId: string) => void;
  onNodeDblClick?: (nodeId: string) => void;
}

const GraphView: React.FC<GraphViewProps> = ({ graphData, onNodeClick, onNodeDblClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<Core | null>(null);

  const graphLayout = useUIStore((s) => s.graphLayout);
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const updateNodePosition = useUpdateNodePosition(activeWorkspace?.id ?? '');

  const applyLayout = useCallback((cy: Core, layout: string) => {
    const layoutOptions: Record<string, cytoscape.LayoutOptions> = {
      fcose: {
        name: 'fcose',
        quality: 'proof',
        randomize: false,
        animate: true,
        animationDuration: 600,
        nodeSeparation: 75,
      } as cytoscape.LayoutOptions,
      dagre: {
        name: 'dagre',
        rankDir: 'TB',
        nodeSep: 60,
        edgeSep: 20,
        rankSep: 80,
        animate: true,
        animationDuration: 600,
      } as cytoscape.LayoutOptions,
      cola: {
        name: 'cola',
        animate: true,
        animationDuration: 800,
        nodeSpacing: 40,
      } as cytoscape.LayoutOptions,
      concentric: {
        name: 'concentric',
        animate: true,
        animationDuration: 600,
        concentric: (ele: NodeSingular) => ele.data('strength') as number || 50,
        levelWidth: () => 20,
      } as cytoscape.LayoutOptions,
      grid: { name: 'grid', animate: true, animationDuration: 600 },
      circle: { name: 'circle', animate: true, animationDuration: 600 },
    };

    const opts = layoutOptions[layout] ?? layoutOptions['fcose'];
    cy.layout(opts).run();
  }, []);

  // Initialize Cytoscape
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const cy = cytoscape({
      container: containerRef.current,
      elements: buildElements(graphData),
      style: buildStylesheet(),
      wheelSensitivity: 0.3,
      minZoom: 0.1,
      maxZoom: 4,
      boxSelectionEnabled: true,
    });

    cyRef.current = cy;

    // Events
    cy.on('tap', 'node', (evt) => {
      const nodeId = evt.target.id() as string;
      onNodeClick?.(nodeId);

      // Highlight neighbours
      const connected = evt.target.closedNeighborhood();
      cy.elements().addClass('faded');
      connected.removeClass('faded').addClass('highlighted');
    });

    cy.on('dblclick taphold', 'node', (evt) => {
      const nodeId = evt.target.id() as string;
      onNodeDblClick?.(nodeId);
    });

    cy.on('tap', (evt) => {
      if (evt.target === cy) {
        cy.elements().removeClass('faded highlighted');
      }
    });

    // Persist node positions on drag end
    cy.on('dragfree', 'node', (evt) => {
      const node = evt.target;
      const pos = node.position() as { x: number; y: number };
      updateNodePosition.mutate({ nodeId: node.id() as string, position: pos });
    });

    // Initial layout
    applyLayout(cy, graphLayout);

    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update elements when data changes
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) {
      return;
    }

    const elements = buildElements(graphData);
    cy.json({ elements });

    // Re-run layout if no positions were saved
    const nodesWithoutPosition = graphData.nodes.filter(
      (n) => n.position.x === 0 && n.position.y === 0
    );
    if (nodesWithoutPosition.length > 0) {
      applyLayout(cy, graphLayout);
    }
  }, [graphData, graphLayout, applyLayout]);

  // Re-run layout when layout selection changes
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) {
      return;
    }
    applyLayout(cy, graphLayout);
  }, [graphLayout, applyLayout]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-surface-50 dark:bg-surface-950"
      aria-label="Capability graph visualization"
    />
  );
};

export default GraphView;
